import {
  LRS_DOCUMENT_TYPE,
  NOT_FAILED_FILE_STATUSES,
} from '@packages/domain/constants/documents';
import type { Document } from 'models';
import type { RootState } from 'reducers/types';
import { isDelivered, isReceived } from 'selectors';

const AVAILABLE_FILE_STATUS = 'available';
const INVALID_FILE_STATUS = 'invalid';
const VOID_FILE_STATUS = 'void';
const DOC_VERIFY_WITH_ERRORS_STATUS = 'doc_verify_with_errors';
const DOC_VERIFY_IN_PROGRESS = 'doc_verify_in_progress';
const AVAILABLE_STATUSES = [
  AVAILABLE_FILE_STATUS,
  VOID_FILE_STATUS,
  DOC_VERIFY_WITH_ERRORS_STATUS,
  INVALID_FILE_STATUS,
];

const sortDocuments = (a: Document, b: Document) => {
  if (a.sortOrder < b.sortOrder) {
    return -1;
  }
  if (a.sortOrder > b.sortOrder) {
    return 1;
  }
  return 0;
};

export const documentsCollection = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const sortedDocumentsWithoutLRS = [...(documentsCollection?.documents ?? [])]
    .sort(sortDocuments)
    .filter(d => d.type !== LRS_DOCUMENT_TYPE);

  return {
    ...documentsCollection,
    documents: sortedDocumentsWithoutLRS,
  };
};

export const hasLRSInDocumentCollection = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  return documentsCollection?.documents?.some(
    d => d.type === LRS_DOCUMENT_TYPE,
  );
};

const hasFilesWithNotFailedStatus = (document: Document) => {
  const { files } = document;

  return files.some(file => NOT_FAILED_FILE_STATUSES.includes(file.status));
};

export const allRequiredFilesUploaded = (state: RootState) => {
  const {
    entities: { documentsCollection: { documents } = {} },
  } = state;

  const requiredDocuments = documents?.filter(document => document.required);
  return requiredDocuments?.every(document =>
    hasFilesWithNotFailedStatus(document),
  );
};

export const allCollectionFilesUploaded = (state: RootState) => {
  const { documents } = documentsCollection(state);

  return documents?.every(document => hasFilesWithNotFailedStatus(document));
};

const hasDocumentsCollection = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  return Object.keys(documentsCollection).length > 0;
};

export const requiresDocuments = (state: RootState) => {
  if (isReceived(state) || isDelivered(state)) {
    return false;
  }

  return hasDocumentsCollection(state);
};

export const allFilesAnalyzed = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const documents = documentsCollection.documents;

  if (!documents) {
    return true;
  }

  const allFilesAvailable = documents.some(document => {
    const { files } = document;
    return files.some(file => !AVAILABLE_STATUSES.includes(file.status));
  });

  return !allFilesAvailable;
};

export const hasFilesInDocVerifyProcess = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const documents = documentsCollection.documents;

  return documents.some(document => {
    const { files } = document;
    return files.some(file => file.status === DOC_VERIFY_IN_PROGRESS);
  });
};

export const isDocVerifyActive = (state: RootState) => {
  const {
    entities: {
      documentsCollection: { docVerifyActive = false },
    },
  } = state;

  return docVerifyActive;
};
